/**
 * The sole DOM/event bridge to the captured editor dependency.
 *
 * Supported integration contract: the mockup trigger's aria-label, model menu
 * class names, and a pointerdown event consumed by the captured Radix menu.
 * Direct commands use the explicit studio bridge; DOM fallback only opens the
 * native picker and presents its catalog. No React fibers are inspected.
 * If that dependency changes, update this module and its CSS selectors together.
 */
const SELECTORS = Object.freeze({
  trigger: 'button[aria-label="Change mockup"]',
  menu: '.cp-device-menu',
  row: '.cp-device-row',
  rowName: '.cp-mockup-name',
  badge: '.cp-device-badge',
  group: '.cp-device-group',
  modelsButton: '[data-local-models]',
  legacyCollection: '[data-local-collection]',
  heading: '[data-studio-library-heading]'
});

/** Pure compatibility decision, independently testable from a running renderer. */
export function libraryRowState(registry, label) {
  const model = registry.resolve(label);
  return {model, hidden: model?.available !== true};
}

export class CapturedEditorAdapter {
  constructor(document) {
    this.document = document;
    this.window = document.defaultView;
    this.listeners = new this.window.AbortController();
    this.hiddenOriginals = new Map();
    this.finishOriginals = new Map();
    this.addedElements = new Set();
    this.bridge = null;
    this.stopBridge = null;
    this.registry = null;
  }

  get ready() { return !!this.document.querySelector(SELECTORS.trigger); }
  get selectedLabel() {
    if (this.bridge?.ready) {
      const state = this.bridge.getState();
      const model = this.registry.resolve(state.modelId || 'flat');
      if (model) return model.label;
    }
    return this.document.querySelector(SELECTORS.trigger)?.textContent || '';
  }
  get menu() { return this.document.querySelector(SELECTORS.menu); }

  connectBridge(bridge, registry, onChange) {
    this.disconnectBridge();
    this.bridge = bridge;
    this.registry = registry;
    this.stopBridge = bridge.subscribe(onChange);
  }

  disconnectBridge() {
    this.stopBridge?.();
    this.stopBridge = null;
    this.bridge = null;
    this.registry = null;
  }

  requireBridge() {
    if (!this.bridge?.ready) throw new Error('Studio viewport is not ready');
    return this.bridge;
  }

  /** Direct owned commands preserve native preparation, commits/history/save. */
  async selectModel(value) {
    const bridge = this.requireBridge();
    const model = this.registry.resolve(value);
    if (model?.available !== true) throw new Error(`Model is not available locally: ${String(value)}`);
    return bridge.selectModel(model.kind === 'flat' ? '' : model.id);
  }

  getPanelState() { return this.requireBridge().getState(); }
  getPanelValue(path) { return this.getPanelState().committedValues[path]; }
  setPanelValues(patch) { return this.requireBridge().commit(patch); }

  toggleModels = () => {
    const trigger = this.document.querySelector(SELECTORS.trigger);
    if (!trigger) return false;
    trigger.dispatchEvent(new this.window.PointerEvent('pointerdown', {
      bubbles: true, button: 0, pointerType: 'mouse', ctrlKey: false
    }));
    return true;
  };

  setHidden(element, hidden) {
    if (!this.hiddenOriginals.has(element)) this.hiddenOriginals.set(element, element.hidden);
    if (element.hidden !== hidden) element.hidden = hidden;
  }

  setNativeFinish(nativeFinish) {
    for (const button of this.document.querySelectorAll('button')) {
      if (!/^\s*Finish/i.test(button.textContent)) continue;
      if (!this.finishOriginals.has(button)) this.finishOriginals.set(button, button.style.display);
      const value = nativeFinish ? 'none' : this.finishOriginals.get(button);
      if (button.style.display !== value) button.style.display = value;
    }
  }

  syncModelsButton() {
    let button = this.document.querySelector(SELECTORS.modelsButton);
    if (!button) {
      const info = [...this.document.querySelectorAll('button')]
        .find(element => element.textContent.trim().toLowerCase() === 'info');
      if (!info) return;
      this.document.querySelector(SELECTORS.legacyCollection)?.remove();
      button = this.document.createElement('button');
      button.type = 'button';
      button.textContent = 'Models';
      button.dataset.localModels = '';
      button.setAttribute('aria-label', 'Browse models');
      button.setAttribute('aria-haspopup', 'menu');
      button.addEventListener('click', this.toggleModels, {signal: this.listeners.signal});
      info.after(button);
      this.addedElements.add(button);
      this.document.title = 'Mockup Studio';
    }
    button.setAttribute('aria-expanded', String(!!this.menu));
  }

  syncMenu(registry) {
    const menu = this.menu;
    if (!menu) return;
    menu.setAttribute('aria-label', 'Studio models');
    for (const row of menu.querySelectorAll(SELECTORS.row)) {
      const label = row.querySelector(SELECTORS.rowName)?.textContent || '';
      this.setHidden(row, libraryRowState(registry, label).hidden);
      for (const badge of row.querySelectorAll(SELECTORS.badge)) this.setHidden(badge, true);
    }
    for (const group of menu.querySelectorAll(SELECTORS.group)) {
      const visible = [...group.querySelectorAll(SELECTORS.row)].some(row => !row.hidden);
      this.setHidden(group, !visible);
    }
    let heading = menu.querySelector(SELECTORS.heading);
    if (!heading) {
      heading = this.document.createElement('div');
      heading.dataset.studioLibraryHeading = '';
      const title = this.document.createElement('strong');
      title.textContent = 'Your model library';
      const detail = this.document.createElement('span');
      detail.textContent = registry.librarySummary();
      const close = this.document.createElement('button');
      close.type = 'button';
      close.textContent = '×';
      close.setAttribute('aria-label', 'Close model library');
      close.addEventListener('click', this.toggleModels, {signal: this.listeners.signal});
      heading.append(title, detail, close);
      menu.prepend(heading);
      this.addedElements.add(heading);
    }
    // Drop references to replaced captured menus, so long editing sessions do not
    // retain one detached DOM tree for every time the user opened the library.
    for (const element of this.hiddenOriginals.keys()) {
      if (!element.isConnected) this.hiddenOriginals.delete(element);
    }
    for (const element of this.finishOriginals.keys()) {
      if (!element.isConnected) this.finishOriginals.delete(element);
    }
    for (const element of this.addedElements) {
      if (!element.isConnected) this.addedElements.delete(element);
    }
  }

  /** Observe DOM changes only; attribute changes made by sync cannot recurse. */
  observe(callback) {
    const observer = new this.window.MutationObserver(callback);
    observer.observe(this.document.documentElement, {
      childList: true, characterData: true, subtree: true
    });
    return () => observer.disconnect();
  }

  dispose() {
    this.disconnectBridge();
    this.listeners.abort();
    for (const [element, hidden] of this.hiddenOriginals) element.hidden = hidden;
    for (const [element, display] of this.finishOriginals) element.style.display = display;
    for (const element of this.addedElements) element.remove();
    this.hiddenOriginals.clear();
    this.finishOriginals.clear();
    this.addedElements.clear();
  }
}
